import { colord, extend } from 'colord';
import a11yPlugin from 'colord/plugins/a11y';

extend([a11yPlugin]);

/** Couleur OKLCH décomposée. `h` est en degrés (0-360). */
export interface Oklch {
  l: number;
  c: number;
  h: number;
}

/**
 * Convertit une couleur hex en chaîne oklch pour les CSS variables shadcn.
 * Format: "L C H" (ex: "0.205 0.042 265.755")
 */
export function hexToOklch(hex: string): string {
  const { l, c, h } = hexToOklchValues(hex);
  return `${round(l, 3)} ${round(c, 3)} ${round(h, 3)}`;
}

/**
 * Convertit une couleur hex en composantes OKLCH.
 * Utilise le chemin sRGB → linear RGB → OKLab → OKLCH.
 */
export function hexToOklchValues(hex: string): Oklch {
  const c = colord(hex);
  const { r, g, b } = c.toRgb();

  // sRGB → linear RGB
  const lr = srgbToLinear(r / 255);
  const lg = srgbToLinear(g / 255);
  const lb = srgbToLinear(b / 255);

  // linear RGB → OKLab
  const l_ = 0.4122214708 * lr + 0.5363325363 * lg + 0.0514459929 * lb;
  const m_ = 0.2119034982 * lr + 0.6806995451 * lg + 0.1073969566 * lb;
  const s_ = 0.0883024619 * lr + 0.2817188376 * lg + 0.6299787005 * lb;

  const l = Math.cbrt(l_);
  const m = Math.cbrt(m_);
  const s = Math.cbrt(s_);

  const okL = 0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s;
  const okA = 1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s;
  const okB = 0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s;

  // OKLab → OKLCH
  const C = Math.sqrt(okA * okA + okB * okB);
  let H = (Math.atan2(okB, okA) * 180) / Math.PI;
  if (H < 0) H += 360;

  return { l: okL, c: C, h: H };
}

/**
 * Convertit des composantes OKLCH en hex sRGB (chemin inverse de
 * `hexToOklchValues`). Les couleurs hors gamut sont clampées sur sRGB.
 */
export function oklchToHex({ l, c, h }: Oklch): string {
  const hRad = (h * Math.PI) / 180;
  const okA = c * Math.cos(hRad);
  const okB = c * Math.sin(hRad);

  // OKLab → LMS
  const l_ = l + 0.3963377774 * okA + 0.2158037573 * okB;
  const m_ = l - 0.1055613458 * okA - 0.0638541728 * okB;
  const s_ = l - 0.0894841775 * okA - 1.2914855480 * okB;

  const L = l_ * l_ * l_;
  const M = m_ * m_ * m_;
  const S = s_ * s_ * s_;

  // LMS → linear RGB
  const lr = 4.0767416621 * L - 3.3077115913 * M + 0.2309699292 * S;
  const lg = -1.2684380046 * L + 2.6097574011 * M - 0.3413193965 * S;
  const lb = -0.0041960863 * L - 0.7034186147 * M + 1.7076147010 * S;

  return colord({
    r: linearToSrgb255(lr),
    g: linearToSrgb255(lg),
    b: linearToSrgb255(lb),
  }).toHex();
}

/**
 * Reproduit `color-mix(in oklch, a <weightA>%, b)` du CSS.
 *
 * Détail important : quand une des deux couleurs est achromatique
 * (chroma 0 — `black`, ou un gris `oklch(0.17 0 none)`), sa teinte est
 * « powerless » côté CSS et c'est celle de l'autre couleur qui est
 * conservée. Sans ça le mélange dérive vers une teinte parasite.
 */
export function mixOklch(a: Oklch, b: Oklch, weightA: number): Oklch {
  const w = Math.min(Math.max(weightA, 0), 1);
  let h: number;
  if (a.c === 0) h = b.h;
  else if (b.c === 0) h = a.h;
  else {
    // Interpolation de teinte par l'arc le plus court (défaut CSS).
    let delta = b.h - a.h;
    if (delta > 180) delta -= 360;
    if (delta < -180) delta += 360;
    h = (a.h + delta * (1 - w) + 360) % 360;
  }
  return {
    l: a.l * w + b.l * (1 - w),
    c: a.c * w + b.c * (1 - w),
    h,
  };
}

function srgbToLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

function linearToSrgb255(c: number): number {
  const s = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow(Math.max(c, 0), 1 / 2.4) - 0.055;
  return Math.round(Math.min(Math.max(s, 0), 1) * 255);
}

function round(n: number, d: number): number {
  const f = Math.pow(10, d);
  return Math.round(n * f) / f;
}

/* ------------------------------------------------------------------ */
/*  APCA — choix de la POLARITÉ du label (clair vs sombre)             */
/* ------------------------------------------------------------------ */

/**
 * Contraste perçu APCA (brouillon WCAG 3), en Lc.
 *
 * Pourquoi ne pas se contenter du ratio WCAG 2 ? Sur les couleurs
 * saturées de milieu de gamme (bleu, orange, vert…) la formule WCAG 2
 * sur-évalue le NOIR : un bleu de marque `#1f6fff` donne 4,77 en noir
 * contre 4,39 en blanc, alors que l'œil lit très nettement mieux le
 * blanc (APCA : 75 en blanc contre 34 en noir). C'est exactement le cas
 * signalé — un libellé noir sur le bouton bleu. APCA sert donc à choisir
 * la polarité ; WCAG 2 AA reste la garantie chiffrée (voir
 * `solidBrandSurface`, qui assombrit le fond jusqu'à ce que le blanc
 * atteigne 4,5:1).
 */
function apcaLc(textHex: string, bgHex: string): number {
  const y = (hex: string) => {
    const { r, g, b } = colord(hex).toRgb();
    const lin = (v: number) => Math.pow(v / 255, 2.4);
    return 0.2126729 * lin(r) + 0.7151522 * lin(g) + 0.072175 * lin(b);
  };
  // Soft-clamp des niveaux quasi noirs (APCA-W3).
  const clamp = (v: number) => (v > 0.022 ? v : v + Math.pow(0.022 - v, 1.414));

  const yTxt = clamp(y(textHex));
  const yBg = clamp(y(bgHex));
  if (Math.abs(yBg - yTxt) < 0.0005) return 0;

  if (yBg > yTxt) {
    // Texte sombre sur fond clair.
    const s = (Math.pow(yBg, 0.56) - Math.pow(yTxt, 0.57)) * 1.14;
    return (s < 0.1 ? 0 : s - 0.027) * 100;
  }
  // Texte clair sur fond sombre.
  const s = (Math.pow(yBg, 0.65) - Math.pow(yTxt, 0.62)) * 1.14;
  return (s > -0.1 ? 0 : s + 0.027) * 100;
}

/**
 * `true` si un libellé BLANC se lit mieux qu'un libellé noir sur ce fond
 * (jugé à la perception, pas au ratio WCAG 2). Vrai pour les marques
 * bleues / oranges / vertes / rouges / violettes saturées ; faux pour les
 * teintes claires (jaune, doré, menthe) où le noir est le bon choix.
 */
export function prefersWhiteLabel(bgHex: string): boolean {
  return Math.abs(apcaLc('#ffffff', bgHex)) >= Math.abs(apcaLc('#000000', bgHex));
}

/**
 * Couleur RÉELLE d'un aplat de marque (bouton primaire, badge plein…).
 *
 * Quand le blanc est la bonne polarité mais n'atteint pas AA sur la
 * couleur brute, on assombrit l'aplat du minimum nécessaire — la teinte
 * et la saturation sont conservées, la marque reste reconnaissable, et
 * le libellé peut rester blanc. Sur une marque claire (jaune, doré) on
 * ne touche à rien : le libellé noir y est à la fois lisible et attendu.
 *
 * Même principe que la pastille du menu (`sidebarSectionActiveColor`),
 * appliqué cette fois à `--primary`.
 */
export function solidBrandSurface(
  brandHex: string,
  /**
   * Polarité imposée. En dark mode l'aplat est recalculé : c'est la couleur
   * de marque D'ORIGINE qui doit décider blanc vs noir, sinon une marque
   * verte ou orange bascule en libellé noir dans le seul thème sombre — les
   * deux thèmes n'écriraient pas le bouton pareil.
   */
  preferWhite = prefersWhiteLabel(brandHex),
): string {
  const hex = colord(brandHex).toHex();
  if (!preferWhite) return hex;
  const brand = hexToOklchValues(hex);
  return fitLightness(brand, Math.min(brand.l, whiteAALightness(brand)));
}

/**
 * Variante dark de l'aplat de marque.
 *
 * Sur fond sombre, un aplat trop foncé disparaît (une marque navy se
 * confondrait avec la carte). On monte donc la luminosité JUSQU'AU seuil
 * où le blanc reste AA — le plus lumineux qui reste lisible. À teinte et
 * chroma constants : la marque garde son éclat, contrairement à un
 * mélange vers le noir qui délave la couleur.
 */
export function solidBrandSurfaceForDark(
  brandHex: string,
  preferWhite = prefersWhiteLabel(brandHex),
  /** Repli quand le noir est la bonne polarité (marque claire). */
  lightenedHex = brandHex,
): string {
  if (!preferWhite) return colord(lightenedHex).toHex();
  const brand = hexToOklchValues(colord(brandHex).toHex());
  return fitLightness(brand, whiteAALightness(brand));
}

/**
 * Luminosité OKLCH maximale (teinte + chroma inchangés) pour laquelle un
 * libellé blanc atteint WCAG AA. Recherche dichotomique sur la couleur
 * RÉELLEMENT rendue (donc après clamp sRGB), pour que le seuil tienne
 * même sur les couleurs hors gamut.
 */
function whiteAALightness(color: Oklch): number {
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    if (isContrastAA(oklchToHex({ ...color, l: mid }), '#ffffff')) lo = mid;
    else hi = mid;
  }
  return lo;
}

/**
 * Applique une luminosité en conservant teinte et chroma, avec un filet de
 * sécurité : si le clamp sRGB fait repasser le blanc sous AA, on assombrit
 * par petits pas jusqu'à repasser au-dessus.
 */
function fitLightness(color: Oklch, l: number): string {
  const out = toGamut({ ...color, l });
  const hex = oklchToHex(out);
  return isContrastAA(hex, '#ffffff') ? hex : oklchToHex(darkenUntilWhiteAA(out));
}

/**
 * Ramène une couleur dans le gamut sRGB en réduisant le CHROMA à
 * luminosité et teinte constantes (mapping CSS Color 4). Sans ça,
 * `oklchToHex` clampe canal par canal : la couleur revient dans le gamut
 * mais sa teinte dérive de plusieurs degrés — un orange de marque vire au
 * rouge.
 */
function toGamut(color: Oklch): Oklch {
  let out = color;
  for (let i = 0; i < 60 && !isInGamut(out); i++) {
    out = { ...out, c: out.c * 0.96 };
  }
  return out;
}

/** Vrai si l'aller-retour oklch → hex → oklch est fidèle (donc pas de clamp). */
function isInGamut(color: Oklch): boolean {
  const back = hexToOklchValues(oklchToHex(color));
  if (Math.abs(back.l - color.l) >= 0.004) return false;
  // Sur une couleur quasi neutre la teinte n'a pas de sens : on ne la teste pas.
  if (color.c < 0.01) return true;
  const dh = ((back.h - color.h + 540) % 360) - 180;
  return Math.abs(dh) < 0.4;
}

export interface BrandThemeColors {
  primary: string;
  primaryForeground: string;
  ring: string;
  primaryHover: string;
}

export interface BrandTheme {
  light: BrandThemeColors;
  dark: BrandThemeColors;
}

/**
 * Génère un thème complet à partir d'une couleur primaire hex.
 *
 * - Calcule la foreground (blanc ou noir) selon le contraste WCAG AA
 * - Génère une variante hover plus claire (light mode)
 * - Génère une variante hover plus foncée (dark mode)
 * - Ajuste la luminosité pour le dark mode si trop sombre
 */
export function generateBrandTheme(primaryHex: string): BrandTheme {
  const base = colord(primaryHex);
  // Polarité décidée une seule fois, sur la marque brute, pour que light et
  // dark écrivent le bouton de la même façon.
  const whiteLabel = prefersWhiteLabel(base.toHex());

  // Light mode — l'aplat passe par `solidBrandSurface` : sur une marque
  // saturée il est assombri juste ce qu'il faut pour porter un libellé
  // BLANC (sinon `pickForeground` bascule en noir, illisible en pratique).
  const lightPrimary = colord(solidBrandSurface(base.toHex(), whiteLabel));
  const lightForeground = pickForeground(lightPrimary.toHex());
  const lightHover = lightPrimary.lighten(0.08);

  // Dark mode : on éclaircit si la couleur est trop sombre.
  //
  // Règles :
  //  - Pour un primary GRIS (saturation < 10), on garde un gris mais avec
  //    une luminosité ≥ 65 pour rester visible sur fond sombre. Sinon le
  //    icon container `bg-primary/10 text-primary` est invisible.
  //  - Pour un primary coloré, on garantit lightness ≥ 60 dans tous les
  //    cas (les valeurs basses produisent des icônes illisibles à 10 %
  //    d'opacité sur fond sombre).
  const hsl = base.toHsl();
  const darkLightness = hsl.s < 10
    ? Math.max(hsl.l, 65)
    : Math.max(hsl.l + 12, 60);
  //  - Cet éclaircissement pousse presque toutes les marques dans la zone
  //    où un libellé blanc tombe sous AA — le bouton primaire repassait
  //    alors en NOIR (le défaut signalé). Quand le blanc est la bonne
  //    polarité, on prend donc l'aplat le plus lumineux qui garde le blanc
  //    AA plutôt que la version éclaircie ; les marques à libellé noir
  //    (jaune, doré, menthe) gardent l'éclaircissement, qui les sert.
  const darkPrimary = colord(
    solidBrandSurfaceForDark(
      base.toHex(),
      whiteLabel,
      colord({ h: hsl.h, s: hsl.s, l: Math.min(darkLightness, 75) }).toHex(),
    ),
  );
  const darkForeground = pickForeground(darkPrimary.toHex());
  const darkHover = darkPrimary.darken(0.08);

  return {
    light: {
      primary: lightPrimary.toHex(),
      primaryForeground: lightForeground,
      ring: lightPrimary.toHex(),
      primaryHover: lightHover.toHex(),
    },
    dark: {
      primary: darkPrimary.toHex(),
      primaryForeground: darkForeground,
      ring: darkPrimary.toHex(),
      primaryHover: darkHover.toHex(),
    },
  };
}

/**
 * Choisit blanc ou noir comme couleur de texte pour assurer le contraste WCAG AA (4.5:1).
 * Préfère le blanc quand il atteint AA (rendu attendu sur une couleur de marque) ;
 * sinon noir ; à défaut, celui des deux qui contraste le mieux.
 */
export function pickForeground(bgHex: string): string {
  const bg = colord(bgHex);
  const contrastWhite = bg.contrast('#ffffff');
  const contrastBlack = bg.contrast('#000000');

  if (contrastWhite >= 4.5) return '#ffffff';
  if (contrastBlack >= 4.5) return '#000000';

  // Fallback : aucune des deux polarités n'atteint AA (couleur de milieu
  // de gamme). On tranche à la perception plutôt qu'au ratio WCAG 2, qui
  // sur-évalue le noir sur les teintes saturées. Les appelants qui
  // veulent la garantie AA passent par `solidBrandSurface`.
  return prefersWhiteLabel(bgHex) ? '#ffffff' : '#000000';
}

/**
 * Vérifie que le couple bg/fg atteint le contraste WCAG AA (4.5:1).
 */
export function isContrastAA(bgHex: string, fgHex: string): boolean {
  return colord(bgHex).contrast(fgHex) >= 4.5;
}

/**
 * Les trois recettes de pastille « section active » du menu, telles que
 * définies dans `apps/erp-web/src/index.css` (--sidebar-section-active).
 * Toute modification du CSS doit être répercutée ici, sinon la couleur de
 * texte calculée ne correspondra plus au fond réel.
 */
export type SidebarPillVariant =
  /** Menu sombre (défaut), application en mode clair — `:root` */
  | 'darkMenuLightMode'
  /** Menu sombre, application en mode sombre — `.dark` */
  | 'darkMenuDarkMode'
  /** Menu clair opt-in — `html[data-sidebar-theme="light"]` */
  | 'lightMenu';

const BLACK: Oklch = { l: 0, c: 0, h: 0 };
/** Surface neutre du menu sombre : oklch(0.17 0 none). */
const DARK_MENU_BASE: Oklch = { l: 0.17, c: 0, h: 0 };

function pillRecipe(primary: Oklch, variant: SidebarPillVariant): Oklch {
  switch (variant) {
    case 'darkMenuLightMode': {
      // --sidebar: color-mix(in oklch, var(--primary) 12%, oklch(0.17 0 none))
      const surface = mixOklch(primary, DARK_MENU_BASE, 0.12);
      // --sidebar-section-active: color-mix(in oklch, var(--primary) 85%, var(--sidebar))
      return mixOklch(primary, surface, 0.85);
    }
    case 'darkMenuDarkMode':
      // color-mix(in oklch, var(--primary) 74%, black)
      return mixOklch(primary, BLACK, 0.74);
    case 'lightMenu':
      // color-mix(in oklch, var(--primary) 88%, black)
      return mixOklch(primary, BLACK, 0.88);
  }
}

/**
 * Assombrit vers le noir (par pas de 2 %) jusqu'à ce qu'un label BLANC
 * atteigne WCAG AA. Le mélange vers le noir réduit L et C ensemble : la
 * teinte de la marque est conservée et le résultat reste dans le gamut.
 * Le pas est volontairement fin : sur une marque qui frôle déjà le seuil
 * (un bleu vif, par ex.) l'écart devient invisible à l'œil.
 */
function darkenUntilWhiteAA(color: Oklch): Oklch {
  let out = color;
  // 150 pas suffisent à atteindre le noir (contraste 21) depuis n'importe
  // quelle couleur — la boucle est bornée, jamais infinie.
  for (let i = 0; i < 150 && !isContrastAA(oklchToHex(out), '#ffffff'); i++) {
    out = mixOklch(out, BLACK, 0.98);
  }
  return out;
}

/**
 * Couleur RÉELLE de la pastille pleine qui marque la section active.
 *
 * Ce n'est jamais la couleur de marque brute : la recette CSS l'assombrit
 * déjà, et on l'assombrit encore si nécessaire pour garantir un label
 * blanc lisible (AA). Sans cette garantie, une marque chaude (orange,
 * ambre, vert clair) basculait sur un label NOIR — le cas signalé : du
 * texte noir sur une pastille orange saturée, illisible en pratique.
 */
export function sidebarSectionActiveColor(
  primaryHex: string,
  variant: SidebarPillVariant,
): string {
  return oklchToHex(darkenUntilWhiteAA(pillRecipe(hexToOklchValues(primaryHex), variant)));
}

/**
 * Couleur du label de la section active. Blanc par construction, puisque
 * `sidebarSectionActiveColor` assombrit la pastille jusqu'à ce que le
 * blanc atteigne AA — on repasse quand même par `pickForeground` pour que
 * les deux valeurs restent cohérentes si la recette évolue.
 */
export function sidebarSectionActiveForeground(
  primaryHex: string,
  variant: SidebarPillVariant,
): string {
  return pickForeground(sidebarSectionActiveColor(primaryHex, variant));
}
